import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { SEO } from '@/components';
import { FiArrowLeft } from 'react-icons/fi';
import { track } from '@/lib/analytics';

// Simple Header for legal pages
function LegalHeader() {
  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link to='/' search={{ hero: undefined }} className='text-white text-2xl font-semibold tracking-tight'>
            Harbor
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white/90 hover:text-white transition-colors font-medium'
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </div>
      </nav>
    </header>
  );
}

const PrivacyPolicy = () => {
  useEffect(() => {
    track('Privacy Policy Screen');
  }, []);

  return (
    <div className='bg-white min-h-screen'>
      <SEO
        title='Privacy Policy | Harbor'
        description="Read Harbor's privacy policy to understand how we collect, use, and protect your personal data in our co-parenting app."
        path='/privacy-policy'
      />
      <LegalHeader />
      <div className='pt-24'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-left'>
          <header className='mb-12 pb-6 border-b border-gray-200'>
            <h1 className='text-4xl font-bold text-gray-900 mb-2'>Privacy Policy</h1>
            <p className='text-gray-500 text-sm'>Last Updated: February 2026</p>
          </header>

          <div className='space-y-8 text-gray-600 text-left'>
            <p>
              This Privacy Policy describes how Harbor ("we," "our," or "us") collects, uses,
              and shares information when you use our mobile application and web services (the "App").
            </p>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>1. Information We Collect</h2>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>1.1 Account Information</h3>
              <p className='mb-4'>When you create an account, we collect:</p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Phone number</li>
                <li>Name (first name and last name, if provided)</li>
                <li>Email address (if provided)</li>
                <li>Profile picture (if provided)</li>
              </ul>
              <p>Account authentication is handled by Clerk, a third-party authentication service.</p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>1.2 Household & Co-Parenting Information</h3>
              <p className='mb-4'>To provide our co-parenting services, we collect:</p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Household member information (names, relationships)</li>
                <li>Children's information (names, ages)</li>
                <li>Expense records and payment information</li>
                <li>Shared calendar events and schedules</li>
                <li>Messages exchanged between co-parents</li>
                <li>Receipt images and attachments</li>
              </ul>

              <h3 className='text-xl font-semibold text-gray-800 mb-3 mt-6'>1.3 Device Information</h3>
              <p className='mb-4'>We may collect certain device information automatically, such as:</p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Device type and operating system</li>
                <li>App version</li>
                <li>Usage statistics and analytics data</li>
                <li>Push notification device tokens (for sending notifications)</li>
                <li>Crash reports and error logs</li>
              </ul>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>2. How We Use Your Information</h2>
              <p className='mb-4'>We use the information we collect to:</p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Provide, maintain, and improve our co-parenting services</li>
                <li>Enable expense tracking and splitting between co-parents</li>
                <li>Facilitate communication between household members</li>
                <li>Manage shared calendars and schedules</li>
                <li>Send you important notifications about your account and household activity</li>
                <li>Process and record payments between co-parents</li>
                <li>Generate expense reports and summaries</li>
                <li>Respond to your comments and questions</li>
              </ul>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>3. Data Sharing</h2>
              <p className='mb-4'>
                Your co-parenting data is shared only with members of your household who you explicitly invite.
                This includes:
              </p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Expense records and payment history with your co-parent(s)</li>
                <li>Shared calendar events</li>
                <li>Messages within your household</li>
                <li>Children's information with authorized household members</li>
              </ul>
              <p>
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>4. SMS/Text Messaging</h2>
              <p className='mb-4'>
                Harbor uses SMS text messages to verify your phone number during sign-up and sign-in.
              </p>
              <p className='mb-4'>
                By providing your phone number and using our services, you consent to receive these
                SMS messages. Message frequency varies. Standard message and data rates may apply.
                You may opt out at any time by replying STOP to any message. Reply HELP for assistance.
              </p>
              <p className='mb-4'>
                <strong>
                  Your mobile information, including your phone number and SMS opt-in data and consent,
                  will not be sold, rented, or shared with any third parties for promotional or marketing
                  purposes.
                </strong>{' '}
                We may share your phone number with service providers who assist us in delivering SMS
                messages (such as our messaging platform provider), solely for the purpose of operating
                our service. These providers are contractually prohibited from using your information
                for any other purpose.
              </p>
              <p>
                All categories of data described in this policy exclude text messaging originator
                opt-in data and consent; this information will not be shared with any third parties.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>5. Data Storage and Security</h2>
              <p>
                Your data is stored securely using industry-standard security measures.
                We implement appropriate technical and organizational measures to protect
                your personal information against unauthorized access, alteration, disclosure,
                or destruction. Financial information and sensitive co-parenting data is encrypted
                both in transit and at rest.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>6. Data Retention</h2>
              <p className='mb-4'>
                We retain your personal information for as long as your account is active or as needed to provide you our services. Specifically:
              </p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Account information is retained until you request account deletion</li>
                <li>Expense records, payment history, and messages are retained for the duration of your account</li>
                <li>If you request account deletion, we will delete your personal data within 30 days</li>
                <li>Some information may be retained longer if required by law or for legitimate business purposes (e.g., fraud prevention, legal claims)</li>
              </ul>
              <p>
                After account deletion, anonymized or aggregated data that cannot identify you may be retained for analytical purposes.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>7. Third-Party Services</h2>
              <p className='mb-4'>The App uses the following third-party services:</p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>
                  <strong>Clerk:</strong> For user authentication and account management
                </li>
                <li>
                  <strong>RevenueCat:</strong> For subscription and in-app purchase management
                </li>
                <li>
                  <strong>Sentry:</strong> For error tracking and crash reporting to improve app stability
                </li>
                <li>
                  <strong>Amplitude:</strong> For anonymous usage analytics to improve the app experience
                </li>
                <li>
                  <strong>Cloud Storage (DigitalOcean Spaces):</strong> For secure storage of receipts and attachments
                </li>
              </ul>
              <p>
                These services have their own privacy policies governing how they collect,
                use, and share your information. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>8. Your Rights</h2>
              <p className='mb-4'>You have the right to:</p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Export your expense and payment data</li>
                <li>Opt out of certain data collection</li>
              </ul>
              <p>
                To exercise these rights, please contact us at{' '}
                <a href='mailto:team@harborcoparent.com' className='text-primary hover:underline'>
                  team@harborcoparent.com
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>9. Children's Privacy</h2>
              <p>
                While Harbor helps parents manage information about their children, the App itself
                is intended for use by adults (parents and guardians). We do not knowingly collect
                personal information directly from children under 13. The children's information
                stored in the App is provided by their parents or guardians for the purpose of
                co-parenting coordination.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>10. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of
                any changes by posting the new Privacy Policy on this page and updating the
                "Last Updated" date. You are advised to review this Privacy Policy periodically
                for any changes.
              </p>
            </section>

            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4 mt-8'>11. Contact Us</h2>
              <p className='mb-4'>
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul className='list-disc list-inside ml-4 mb-4 space-y-2'>
                <li>
                  Email:{' '}
                  <a href='mailto:team@harborcoparent.com' className='text-primary hover:underline'>
                    team@harborcoparent.com
                  </a>
                </li>
              </ul>
            </section>
          </div>

          <footer className='mt-16 pt-6 border-t border-gray-200 text-left text-gray-400 text-sm'>
            <p>&copy; {new Date().getFullYear()} Harbor. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicy,
});

